import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useContext, useCallback, useEffect, useRef } from 'react';
import { Box, Typography, Button, Tabs, Tab, Accordion, AccordionSummary, AccordionDetails, CircularProgress } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { IconContext, useApi, API } from '@plumeuk/shapeshift-identity';
import 'x2js';

const useStyles$9 = makeStyles()((_theme) => ({
    moduleNotAvailable: {
        textAlign: "center",
        paddingTop: "50px"
    },
    errIcon: {
        fontSize: "50px",
        color: "#FFBA39"
    },
    title: {
        fontSize: "46px",
        fontWeight: 700,
        marginBottom: "40px"
    },
    subTitle: {
        fontSize: "21px",
        opacity: "0.5"
    }
}));
const ModuleNotAvailable = ({ className, children, footer, ...props }) => {
    const { classes, cx } = useStyles$9();
    const { icon } = useContext(IconContext);
    return (jsxs(Box, { className: cx(classes.moduleNotAvailable, className), ...props, children: [icon("errorIcon", { className: classes.errIcon }), jsx("br", {}), jsx(Typography, { className: classes.title, variant: "h3", children: "Whoa there, keen bean." }), jsx(Typography, { className: classes.subTitle, variant: "body1", children: children ?? jsx(Fragment, { children: "This lesson isn't available." }) }), footer ?? null] }));
};

const useSetModuleCompletion = () => {
    const [apiResult, getData] = useApi(null);
    const setComplete = (courseSlug, moduleSlug, type, status, config) => getData({
        ...(config ?? {}),
        url: API.moduleComplete(courseSlug, moduleSlug, type, status),
        method: "POST",
        cache: false
    });
    return { response: apiResult.data, apiResult, setComplete };
};

const useStyles$8 = makeStyles()((theme) => ({
    moduleCompleteButton: {
        [theme.breakpoints.down("sm")]: {
            width: "100%"
        }
    }
}));
const ModuleCompleteButton = ({ courseSlug, moduleSlug, type, className, onChange, onApiUpdate, actionOverride, children, ...props }) => {
    const { classes, cx } = useStyles$8();
    const { setComplete, response, apiResult } = useSetModuleCompletion();
    const handleOnClick = actionOverride ?? useCallback(() => {
        setComplete(courseSlug, moduleSlug, type, true);
    }, [courseSlug, moduleSlug]);
    useEffect(() => response && onChange?.(response), [response]);
    useEffect(() => {
        onApiUpdate?.(apiResult);
    }, [apiResult]);
    return (jsx(Button, { "data-test-id": "module-complete-button-submit-btn", className: cx(classes.moduleCompleteButton, className), onClick: handleOnClick, ...props, children: children?.(response, apiResult) ?? jsx(Fragment, { children: "Mark as completed" }) }));
};

const useStyles$7 = makeStyles()((theme) => ({
    outlineButton: {
        border: `${theme.palette.secondary.main} 1px solid`,
        color: theme.palette.secondary.main,
        backgroundColor: "transparent",
        "&:hover": {
            backgroundColor: theme.palette.secondary.main + "10"
        }
    }
}));
const OutlineButton = ({ children, className, ...props }) => {
    const { classes, cx } = useStyles$7();
    return (jsx(Button, { className: cx(classes.outlineButton, className), ...props, children: children }));
};

const useStyles$6 = makeStyles()((_theme) => ({
    moduleUnCompleteButton: {}
}));
const ModuleUnCompleteButton = ({ courseSlug, moduleSlug, type, className, onChange, onApiUpdate, actionOverride, children, ...props }) => {
    const { classes, cx } = useStyles$6();
    const { setComplete, response, apiResult } = useSetModuleCompletion();
    const handleOnClick = actionOverride ?? useCallback(() => {
        setComplete(courseSlug, moduleSlug, type, false);
    }, [courseSlug, moduleSlug]);
    useEffect(() => response && onChange?.(response), [response]);
    useEffect(() => {
        onApiUpdate?.(apiResult);
    }, [apiResult]);
    return (jsx(OutlineButton, { "data-test-id": "module-uncomplete-button", className: cx(classes.moduleUnCompleteButton, className), onClick: handleOnClick, ...props, children: children?.(response, apiResult) ?? jsx(Fragment, { children: "Mark as incomplete" }) }));
};

const MODULE_TABS = [
    {
        label: "Overview",
        value: "overview"
    },
    {
        label: "Comments",
        value: "comments"
    },
    {
        label: "Notes",
        value: "notes"
    },
    {
        label: "Transcript",
        value: "transcript"
    }
];
const useStyles$5 = makeStyles()(() => ({
    tabs: {},
    tab: {
        color: "#A8A8A8",
        fontSize: "18px"
    },
    activeTab: {
        color: "#2A2A2F !important"
    }
}));
const ModuleTabs = ({ className, onChange, tabSelected, moduleTabs, ...props }) => {
    const { classes, cx } = useStyles$5();
    const handleChange = (_event, newValue) => {
        onChange(newValue);
    };
    return (jsx(Box, { className: cx(classes.tabs, className), ...props, children: jsx(Tabs, { variant: "standard", value: tabSelected, onChange: handleChange, children: (moduleTabs ?? MODULE_TABS).map(({ label, value }) => (jsx(Tab, { "data-test-id": `module-tabs-${label?.toLocaleLowerCase()?.replace(" ", "")}`, value: value, label: label, className: cx(classes.tab, tabSelected === value ? classes.activeTab : "") }, value))) }) }));
};

/* eslint-disable no-console */
console.log.bind(null) ;
console.warn.bind(null) ;
console.error.bind(null) ;

const getDueDateString = function (d) {
    const oneDayInMilliseconds = 24 * 60 * 60 * 1000;
    const difference = Math.abs(d.getTime() - new Date().getTime());
    const monthNames = ["January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"];
    if (difference < oneDayInMilliseconds) {
        return "today";
    }
    else if (difference < 6 * oneDayInMilliseconds) {
        const dayOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][d.getDay()];
        return `on ${dayOfWeek}`;
    }
    else {
        return `on ${d.getDate()} ${monthNames[d.getMonth()]} ${d.getFullYear()}`;
    }
};
function toDate(argument) {
    if (isDate(argument)) {
        return new Date(argument.getTime());
    }
    else if (typeof argument === "number") {
        return new Date(argument);
    }
    else {
        return new Date(NaN);
    }
}
function compareAsc(dateLeft, dateRight) {
    const _dateLeft = toDate(dateLeft);
    const _dateRight = toDate(dateRight);
    const diff = _dateLeft.getTime() - _dateRight.getTime();
    if (diff < 0) {
        return -1;
    }
    else if (diff > 0) {
        return 1;
        // Return 0 if diff is 0; return NaN if diff is NaN
    }
    else {
        return diff;
    }
}
function isDate(value) {
    return (value instanceof Date ||
        (typeof value === "object" &&
            Object.prototype.toString.call(value) === "[object Date]"));
}

const useModulesUpcoming = (courseSlug, data) => {
    const [apiResult, getData, setUpcoming] = useApi(data || !courseSlug ? null : API.upcoming(courseSlug), data);
    const getUpcoming = (courseSlugOverride, configOverride) => {
        const slug = courseSlugOverride ?? courseSlug;
        if (!slug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.upcoming(slug) }
            : API.upcoming(slug));
    };
    useEffect(() => data && setUpcoming(data), [data]);
    return { upcoming: apiResult.data, apiResult, getUpcoming, setUpcoming };
};

const useWebinar = (courseSlug, webinarSlug, data) => {
    const [apiResult, getData, setWebinar] = useApi(data || !courseSlug || !webinarSlug ? null : API.webinar(courseSlug, webinarSlug), data);
    const getWebinar = (courseSlugOverride, webinarSlugOverride, configOverride) => {
        const _webinarSlug = webinarSlugOverride ?? webinarSlug;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        if (!_webinarSlug || !_courseSlug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.webinar(_courseSlug, _webinarSlug) }
            : API.webinar(_courseSlug, _webinarSlug));
    };
    useEffect(() => data && setWebinar(data), [data]);
    return { webinar: apiResult.data, apiResult, getWebinar, setWebinar };
};

const ModuleUpcomingDataProvider = ({ children, courseSlug, data }) => {
    const dataHook = useModulesUpcoming(courseSlug, data);
    const { getUpcoming } = dataHook;
    useEffect(() => {
        if (courseSlug && !data)
            getUpcoming(courseSlug);
    }, [courseSlug]);
    return children?.(dataHook)
        ?? jsx("div", { children: "Please implement children to handle upcoming" });
};

const useStyles$4 = makeStyles()((theme) => ({
    checkIcon: {
        borderRadius: 100,
        backgroundColor: theme.palette.secondary.main
    }
}));
const CheckIcon = ({ className, ...props }) => {
    const { classes, cx } = useStyles$4();
    const { icon } = useContext(IconContext);
    return (icon("doneIcon", { className: cx(classes.checkIcon, className), ...props }));
};

const useStyles$3 = makeStyles()((theme) => ({
    contentContainer: {
        display: "flex"
    },
    upcomingModuleItem: {
        padding: "12px 0"
    },
    upcomingModuleIcon: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "start",
        alignItems: "center",
        marginRight: "15px"
    },
    upcomingModuleTitle: {
        display: "flex",
        flexDirection: "column",
        gap: "5px",
        marginTop: "3px"
    },
    upcomingModuleLabel: {
        paddingBottom: "3px",
        fontSize: "16px",
        fontWeight: "500"
    },
    upcomingModuleFooter: {
        fontSize: "15px",
        opacity: "0.5"
    },
    upcomingModuleIconCheck: {
        padding: "4px",
        width: "14px",
        height: "14px",
        borderRadius: 100
    },
    upcomingModuleIconCircle: {
        padding: "4px",
        width: "14px",
        height: "14px",
        backgroundColor: theme.palette.primary.dark,
        fill: theme.palette.primary.dark,
        borderRadius: 100
    }
}));
const UpcomingModuleItem = ({ typeIcons, className, onClick, onGoToModuleAction, module, ...props }) => {
    const { classes, cx } = useStyles$3();
    const currentDate = new Date();
    const dueDate = module?.dueDate ? new Date(module?.dueDate) : null;
    const isDue = !!(dueDate && isDate(dueDate) && compareAsc(dueDate, currentDate) > 0);
    const isOverdue = !!(dueDate && isDate(dueDate) && compareAsc(dueDate, currentDate) < 0);
    const isComplete = module?.complete || false;
    const activeIconRef = useRef(null);
    const { icon } = useContext(IconContext);
    let tooltipContent = "";
    if (isComplete) {
        tooltipContent = "Completed";
    }
    else if (isDue && dueDate && isDate(dueDate)) {
        tooltipContent = `Due ${getDueDateString(dueDate)}`;
    }
    else if (isOverdue) {
        tooltipContent = "Overdue";
    }
    const handleOnClick = useCallback((e) => {
        onClick?.(e);
        onGoToModuleAction(module);
    }, [onGoToModuleAction, onClick]);
    return (jsx(Box, { "data-test-id": `upcoming-module-item-${module.moduleId}`, onClick: handleOnClick, className: cx(classes.upcomingModuleItem, className, module?.type), ...props, children: jsxs(Box, { className: classes.contentContainer, children: [jsx(Box, { ref: activeIconRef, className: classes.upcomingModuleIcon, children: module?.complete ? (jsx(CheckIcon, { className: classes.upcomingModuleIconCheck, style: { color: "white" } })) : (icon("circleIcon", {
                        className: classes.upcomingModuleIconCircle
                    })) }), typeIcons?.find(e => e.type === module?.type)?.icon, jsxs(Box, { className: classes.upcomingModuleTitle, children: [jsx(Box, { className: classes.upcomingModuleLabel, children: module?.title }), tooltipContent && (jsx(Box, { className: classes.upcomingModuleFooter, children: tooltipContent }))] })] }) }));
};

const useStyles$2 = makeStyles()((_theme) => ({
    upcomingSectionItem: {},
    sectionName: {
        paddingRight: "25px"
    },
    accordionSummary: {
        padding: 0
    }
}));
const UpcomingSectionItem = ({ typeIcons, className, section, onGoToModuleAction, modules, ...props }) => {
    const { expandMoreIcon } = useContext(IconContext);
    const { classes, cx } = useStyles$2();
    return (jsx(Box, { className: cx(classes.upcomingSectionItem, className), ...props, children: jsxs(Accordion, { defaultExpanded: true, children: [jsx(AccordionSummary, { expandIcon: expandMoreIcon, className: classes.accordionSummary, "aria-controls": "panel1a-content", defaultChecked: true, id: "panel1a-header", children: jsx(Typography, { className: classes.sectionName, variant: "h3", children: section }) }), jsx(AccordionDetails, { children: modules?.map((e, i) => (jsx(UpcomingModuleItem, { "data-test-id": `upcoming-module-item-${e.moduleId}`, typeIcons: typeIcons, module: e, onGoToModuleAction: onGoToModuleAction }, i))) })] }) }));
};

const replaceAll = (str, find, replace) => {
    // Escape special characters in `find` using a regular expression
    const escapedFind = find.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    // Create a regular expression with global flag to replace all occurrences
    const regex = new RegExp(escapedFind, "g");
    // Use the replace method with the regular expression
    return (str ?? "").replace(regex, replace);
};

const useStyles$1 = makeStyles()(() => ({
    availableModules: {}
}));
const UpcomingModules = ({ className, courseSlug, typeIcons, data, onGoToModuleAction, ...props }) => {
    const { classes, cx } = useStyles$1();
    return (jsx(Box, { className: cx(classes.availableModules, className), ...props, children: jsx(ModuleUpcomingDataProvider, { courseSlug: courseSlug, data: data, children: ({ upcoming }) => {
                const modulesBySectionMap = {};
                const modulesWithoutSection = [];
                upcoming?.forEach(e => {
                    if (e.section)
                        modulesBySectionMap[e.section] = modulesBySectionMap[e.section] ? [...modulesBySectionMap[e.section], e] : [e];
                    else
                        modulesWithoutSection.push(e);
                });
                return jsxs(Fragment, { children: [Object.keys(modulesBySectionMap)?.map((e, i) => (jsx(UpcomingSectionItem, { "data-test-id": `upcoming-section-item-${replaceAll(e, " ", "")?.toLocaleLowerCase()}`, section: e, modules: modulesBySectionMap[e], typeIcons: typeIcons, onGoToModuleAction: onGoToModuleAction }, i))), modulesWithoutSection?.map((e, i) => (jsx(UpcomingModuleItem, { "data-test-id": `upcoming-module-item-${e.moduleId}`, module: e, typeIcons: typeIcons, onGoToModuleAction: onGoToModuleAction }, i)))] });
            } }) }));
};

const useStyles = makeStyles()(() => ({
    upcomingWebinarSpotlightContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column"
    },
    upcomingWebinarSpotlightIconContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        "& > *": {
            width: "138px",
            height: "138px"
        }
    },
    upcomingWebinarSpotlightTitle: {
        marginTop: "4px"
    },
    upcomingWebinarSpotlightDescription: {
        marginBottom: "4px"
    },
    upcomingWebinarSpotlightButton: {
        margin: "10px 0",
        height: "24px"
    }
}));
const UpcomingWebinarSpotlight = ({ className, Icon, onGotToWebinarAction, courseSlug, ...props }) => {
    const { classes, cx } = useStyles();
    const { upcoming, apiResult: upcomingApiResult, getUpcoming } = useModulesUpcoming();
    const { webinar, apiResult: webinarApiResult, getWebinar } = useWebinar();
    useEffect(() => {
        if (!upcoming)
            return;
        const nextWebinar = upcoming.find(e => e.type === "webinar");
        if (!nextWebinar)
            return;
        getWebinar(courseSlug, nextWebinar?.moduleSlug);
    }, [upcoming]);
    useEffect(() => {
        getUpcoming(courseSlug);
    }, [getUpcoming, courseSlug]);
    if (webinarApiResult.isLoading || upcomingApiResult.isLoading) {
        return jsx(Box, { className: cx(classes.upcomingWebinarSpotlightContainer, className), children: jsx(CircularProgress, {}) });
    }
    if (!webinar || !courseSlug) {
        return jsx(Fragment, {});
    }
    const webinarDateTime = new Date(webinar.startDateTime);
    return (jsxs(Box, { className: cx(classes.upcomingWebinarSpotlightContainer, className), ...props, children: [jsx(Box, { className: classes.upcomingWebinarSpotlightIconContainer, children: Icon }), jsx(Typography, { className: classes.upcomingWebinarSpotlightTitle, children: "Next live class" }), jsx(Typography, { className: classes.upcomingWebinarSpotlightDescription, children: webinarDateTime.toLocaleDateString() }), jsx(Button, { "data-test-id": "upcoming-webinar-spotlight-go-to-event-btn", className: classes.upcomingWebinarSpotlightButton, onClick: () => onGotToWebinarAction(courseSlug, webinar.slug), children: "Go to event" })] }));
};

export { ModuleCompleteButton, ModuleNotAvailable, ModuleTabs, ModuleUnCompleteButton, UpcomingModuleItem, UpcomingModules, UpcomingWebinarSpotlight };
